import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useDoseTaskProcessing } from '~/backend/connector-api/connector-components';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { transformDoseTargets } from '~/utils/transforms/doseTargetsTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useStructureSetCacheUpdate from '../useStructureSetCacheUpdate';

type UseDosePredictionTaskProps = {
    patientId: string;
    imageStudyId: string;
    imageSeriesId: string;
    structureSetInstanceUid: string;
};

/**
 * A hook for creating a dose prediction task via Connector API.
 * @param {UseDosePredictionTaskProps} props - Contains study and series identifiers for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useDosePredictionTask({
    patientId,
    imageStudyId,
    imageSeriesId,
    structureSetInstanceUid,
}: UseDosePredictionTaskProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const dosePredictionTaskOptions = useRecoilValue(dosePredictionTaskState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startDosePrediction } = useDoseTaskProcessing();
    const updateCachedStructureSet = useStructureSetCacheUpdate();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentDose } = useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                updateJob(job.id, { isImported: true });
                setEnvironmentDose(
                    processedItems.doses,
                    selectedPatientId ?? ''
                );
            }
        }
    };
    /**
     * Creates the dose prediction task with the provided model.
     * @param {string} modelName - The model to use for the dose prediction task.
     */
    const start = async (modelName: string) => {
        if (isSubmitting) {
            return;
        }
        if (!dosePredictionTaskOptions?.targets) {
            handleError(new Error('No target defined.'));
            return;
        }

        setIsSubmitting(true);
        try {
            await updateCachedStructureSet();
            const task = await startDosePrediction({
                body: {
                    modelName,
                    imageSeries: {
                        seriesInstanceUID: imageSeriesId,
                        studyInstanceUID: imageStudyId,
                    },
                    structureSet: {
                        sopInstanceUID: structureSetInstanceUid,
                        patientID: patientId,
                    },
                    doseTargets: transformDoseTargets(
                        dosePredictionTaskOptions.targets
                    ),
                },
            });

            const newJob: NewJobData = {
                name: `Dose+`,
                id: task.id,
                type: WorkflowTypes.DOSE,
                task: {
                    targets: dosePredictionTaskOptions.targets,
                    model: dosePredictionTaskOptions.model,
                    output: task,
                },
                onLoad: handleProcessedFiles,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleError(error, 'Failed to start dose prediction task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
